import { Link, NavLink } from 'react-router';
import { useAuth, useUser } from '@clerk/react';
import { useTranslation } from 'react-i18next';
import { BrandWordmark } from '../brand-wordmark/index.js';
import { UserPopover } from './user-popover.js';

// "Installation" routes differently by auth state: unauthenticated visitors get
// the public blog install guide; signed-in users get the interactive
// getting-started page with their synced setup progress.
const INSTALL_HREF_GUEST = '/blog/install-agentbrush-claude-cursor';
const INSTALL_HREF_AUTH = '/getting-started';

export function Header() {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const { t } = useTranslation('common');

  const signedIn = isLoaded && Boolean(isSignedIn);

  // Blog / Installation / Changelog always render so nothing flickers while auth
  // resolves. Dashboard is added only once we know the user is signed in — it
  // never appears for guests, and Blog never disappears under it.
  const entries: { href: string; label: string }[] = [];
  if (signedIn) entries.push({ href: '/dashboard', label: t('nav.overview') });
  entries.push({ href: '/blog', label: t('nav.blog') });
  entries.push({
    href: signedIn ? INSTALL_HREF_AUTH : INSTALL_HREF_GUEST,
    label: t('nav.installation'),
  });
  entries.push({ href: '/changelog', label: t('nav.changelog') });

  const email = user?.primaryEmailAddress?.emailAddress;
  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.username ||
    email?.split('@')[0] ||
    '';

  return (
    <header className="sticky top-0 z-40 border-b border-white/[0.06] bg-canvas/85 backdrop-blur-md">
      <nav
        aria-label={t('nav.mainNavigation')}
        className="mx-auto flex h-14 w-full max-w-[1200px] items-center gap-4 px-6 standard:px-10"
      >
        {/* Brand — left third */}
        <div className="flex flex-1 items-center">
          <Link
            to={signedIn ? '/dashboard' : '/'}
            className="inline-flex items-center whitespace-nowrap text-body outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cyan-400"
          >
            <BrandWordmark />
            <span aria-hidden="true" className="agb-logo-caret" />
          </Link>
        </div>

        {/* Main nav — centered between the two equal-width side slots */}
        <div className="flex items-center gap-6">
          {entries.map((entry) => (
            <HeaderLink key={entry.href} href={entry.href} label={entry.label} />
          ))}
        </div>

        {/* Right — right third */}
        <div className="flex flex-1 items-center justify-end gap-5">
          {signedIn && (
            <>
              {displayName && (
                <span
                  className="hidden truncate text-[13px] text-secondary standard:inline"
                  title={email ?? displayName}
                >
                  {displayName}
                </span>
              )}
              <UserPopover />
            </>
          )}
          {isLoaded && !isSignedIn && (
            <>
              <Link
                to="/sign-in"
                className="rounded-md border border-border-bright bg-transparent px-4 py-1.5 text-sm font-semibold text-secondary transition-colors hover:border-secondary hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
              >
                {t('auth.signIn')}
              </Link>
              <Link
                to="/sign-up"
                className="rounded-md bg-accent-lime px-4 py-1.5 text-sm font-semibold text-canvas transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
              >
                {t('auth.signUp')}
              </Link>
            </>
          )}
        </div>
      </nav>
    </header>
  );
}

interface HeaderLinkProps {
  href: string;
  label: string;
}

function HeaderLink({ href, label }: HeaderLinkProps) {
  return (
    <NavLink
      to={href}
      className={({ isActive }) =>
        [
          'relative whitespace-nowrap text-[13px] transition-colors duration-150',
          'outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cyan-400',
          isActive ? 'text-primary' : 'text-secondary hover:text-primary',
        ].join(' ')
      }
    >
      <span>{label}</span>
    </NavLink>
  );
}
